import {
  returnAllTradeDetailsFromGenericParams,
  returnOPLegFormThisTrade,
  returnRPLegFromThisTrade,
} from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { NotationToDecimalValueType } from "@/lib/interfaces and Enums/TYPENotations";

/**
 * Gets the order type for the current trade
 * For OP at ER level: Retrieved directly from params.data.orderType
 * For OP at Leg level: Retrieved from executionRequestCounterParties[0].orderType
 * For RP: Inferred from referencePrice - if null/undefined, it's a Market order
 *
 * @param params - AG Grid cell params
 * @param thisTrade - The current trade from Zustand store
 * @returns "Market" | "Limit" | "Stop" | null
 */
export function getOrderType(
  params: any,
  thisTrade: ExecutionStrategy | undefined,
): "Market" | "Limit" | null {
  if (!thisTrade) {
    return null;
  }

  const { isOp, isER, fullTradeData } =
    returnAllTradeDetailsFromGenericParams(params);

  if (isOp) {
    // For OP at ER level: orderType is directly on params.data
    if (isER && params.data?.orderType) {
      return params.data.orderType;
    }
    // For OP at Leg level: Get orderType from executionRequestCounterParties
    const orderType = (fullTradeData as any)
      ?.executionRequestCounterParties?.[0]?.orderType;
    return orderType || null;
  } else {
    const orderType = fullTradeData.orderType;
    if (orderType) {
      return orderType;
    }
    return null;
  }
}

/**
 * Gets the reference price/spread value for display
 * Priority order:
 * 1. params.value (AG Grid provides field value directly)
 * 2. params.data direct access (for Orders - referencePrice/referenceSpread)
 * 3. Leg helpers (for RFQ compatibility - do NOT modify this path)
 *
 * @param params - AG Grid cell params
 * @param thisTrade - The current trade from Zustand store
 * @returns The reference price/spread value or null
 */
export function getReferencePrice(
  params: any,
  thisTrade: ExecutionStrategy | undefined,
): string | number | undefined {
  // 1. First check params.value (AG Grid provides the field value directly)
  // Note: Empty string "" should be treated as "no value"
  if (
    params.value !== null &&
    params.value !== undefined &&
    params.value !== ""
  ) {
    return params.value;
  }

  // 2. Check params.data directly (handles Orders where data structure differs)
  const directValue =
    params.data?.referencePrice ?? params.data?.referenceSpread;
  // Note: Empty string "" should be treated as "no value"
  if (directValue !== null && directValue !== undefined && directValue !== "") {
    return directValue;
  }

  // 3. Fall back to leg helpers (RFQ compatibility - do NOT change this logic)
  if (!thisTrade) {
    return undefined;
  }

  const { isOp, isOutright, isER, isLeg, isOrder } =
    returnAllTradeDetailsFromGenericParams(params);

  const helperObject = {
    params,
    thisTrade,
    isOp,
    isER,
    isOutright,
    isLeg,
    isOrder,
  };

  if (isOp) {
    const thisOPLeg = returnOPLegFormThisTrade(helperObject);
    return thisOPLeg?.referencePrice ?? undefined;
  } else {
    const thisRPLeg = returnRPLegFromThisTrade(helperObject);
    return thisRPLeg?.referencePrice ?? undefined;
  }
}

/**
 * Gets the notation for the current leg/row
 * Used for displaying values with correct notation suffix (e.g., "15bps")
 *
 * @param params - AG Grid cell params
 * @param thisTrade - The current trade from Zustand store
 * @returns The notation string (e.g., "bps", "%") or undefined
 */
export function getNotation(
  params: any,
  thisTrade: ExecutionStrategy | undefined,
): NotationToDecimalValueType | undefined {
  // 1. Check params.data directly (handles most cases where notation is on the row)
  if (params.data?.notation) {
    return params.data.notation as NotationToDecimalValueType;
  }

  if (params.data.notationER) {
    return params.data.notationER as NotationToDecimalValueType;
  }

  // 2. Check fullTradeData.legs (available from props/params, works from start)
  const { fullTradeData, isOp, isOutright, isER, isLeg, isOrder } =
    returnAllTradeDetailsFromGenericParams(params);

  // // Try to get notation from fullTradeData.legs first (available immediately)
  // if (fullTradeData?.legs && fullTradeData.legs.length > 0) {
  //   // Try to match by blockId if available
  //   const blockId = params.data?.blockId || params.data?.id;
  //   if (blockId) {
  //     const matchingLeg = fullTradeData.legs.find(
  //       (leg: any) => leg.blockId == blockId,
  //     );
  //     if (matchingLeg?.notation) {
  //       return matchingLeg.notation as NotationToDecimalValueType;
  //     }
  //   }
  //   // Fall back to first leg notation
  //   if (fullTradeData.legs[0]?.notation) {
  //     return fullTradeData.legs[0].notation as NotationToDecimalValueType;
  //   }
  // }

  // 3. Fall back to leg helpers if trade from store exists
  if (!thisTrade) {
    return undefined;
  }

  const helperObject = {
    params,
    thisTrade,
    isOp,
    isER,
    isOutright,
    isLeg,
    isOrder,
  };

  if (isOp) {
    if (!isOutright && fullTradeData.executionRequestCounterParties?.[0]) {
      return fullTradeData.executionRequestCounterParties[0].notation as
        | NotationToDecimalValueType
        | undefined;
    }
    const thisOPLeg = returnOPLegFormThisTrade(helperObject);
    return thisOPLeg?.notation as NotationToDecimalValueType | undefined;
  } else {
    if (!isOutright) {
      return fullTradeData.notationER as NotationToDecimalValueType | undefined;
    }
    const thisRPLeg = returnRPLegFromThisTrade(helperObject);
    return thisRPLeg?.notation as NotationToDecimalValueType | undefined;
  }
}
